import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, User, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();

  const navLinks = [
    { href: '/booking', label: 'Réserver votre séjour' },
    { href: '/events', label: 'Séjours en groupe & Événements' },
    { href: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-[#E5E5E5]" data-testid="navbar">
        <div className="container-custom">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link 
              to="/" 
              className="text-xl tracking-tight"
              data-testid="logo-link"
            >
              <span className="font-['Playfair_Display'] font-semibold text-[#0A0A0A]">Domaine du Normont</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`nav-link link-underline ${isActive(link.href) ? 'active' : ''}`}
                  data-testid={`nav-${link.href.replace('/', '')}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Right Side */}
            <div className="hidden lg:flex items-center gap-4">
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                      <User className="w-4 h-4" />
                      <span className="text-sm">{user.name || user.email}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem asChild>
                      <Link to="/client" className="flex items-center gap-2" data-testid="nav-client-dashboard">
                        Espace client
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleLogout} className="flex items-center gap-2 text-red-600" data-testid="logout-btn">
                      <LogOut className="w-4 h-4" />
                      Déconnexion
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link to="/login">
                  <Button variant="outline" className="border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white" data-testid="login-btn">
                    Espace client
                  </Button>
                </Link>
              )}
              <Link to="/booking">
                <Button className="bg-[#C5A059] hover:bg-[#A6864A] text-white" data-testid="nav-booking-cta">
                  Réserver
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2"
              onClick={() => setMobileMenuOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu" data-testid="mobile-menu">
          <div className="flex justify-between items-center p-6 border-b border-[#E5E5E5]">
            <span className="font-['Playfair_Display'] font-semibold text-xl">Domaine du Normont</span>
            <button onClick={() => setMobileMenuOpen(false)} data-testid="close-mobile-menu">
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="flex-1 p-6">
            <Link 
              to="/" 
              className="mobile-menu-link block"
              onClick={() => setMobileMenuOpen(false)}
            >
              Accueil
            </Link>
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="mobile-menu-link block"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            {user ? (
              <>
                <Link 
                  to="/client" 
                  className="mobile-menu-link block"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Espace client
                </Link>
                {isAdmin && (
                  <Link 
                    to="/admin" 
                    className="mobile-menu-link block"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Administration
                  </Link>
                )}
                <button 
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  className="mobile-menu-link block w-full text-left text-red-600"
                >
                  Déconnexion
                </button>
              </>
            ) : (
              <Link 
                to="/login" 
                className="mobile-menu-link block"
                onClick={() => setMobileMenuOpen(false)}
              >
                Connexion
              </Link>
            )}
          </div>
          <div className="p-6 border-t border-[#E5E5E5]">
            <Link to="/booking" onClick={() => setMobileMenuOpen(false)}>
              <Button className="w-full bg-[#C5A059] hover:bg-[#A6864A] text-white py-4">
                Réserver votre séjour
              </Button>
            </Link>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
